import { supabase } from '../lib/supabase';

/**
 * Email Service using Resend API through Supabase Edge Functions
 * Handles all email notifications for the appointment system
 */
export const emailService = {
  /**
   * Send initial appointment confirmation email to client
   * @param {Object} appointmentData - Appointment details
   * @returns {Promise<Object>} - Email send result
   */
  async sendAppointmentConfirmation(appointmentData) {
    try {
      const {
        appointmentId,
        clientName,
        clientEmail,
        agentName,
        appointmentDate,
        appointmentTime,
        purpose,
        purposeDetails,
        confirmationNumber
      } = appointmentData;

      // Format date and time for email
      const formattedDate = new Date(appointmentDate)?.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      const emailContent = {
        to: clientEmail,
        subject: `Appointment Confirmation - ${confirmationNumber}`,
        html: `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Appointment Confirmation</title>
          </head>
          <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
            <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px;">
              <tr>
                <td align="center">
                  <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                    <!-- Header -->
                    <tr>
                      <td style="background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); padding: 40px 30px; text-align: center;">
                        <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: bold;">Appointment Confirmed</h1>
                        <p style="color: #e0e7ff; margin: 10px 0 0 0; font-size: 16px;">Sailors Bail Bonds</p>
                      </td>
                    </tr>

                    <!-- Confirmation Message -->
                    <tr>
                      <td style="padding: 30px;">
                        <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                          Hi ${clientName},
                        </p>
                        <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                          Your appointment has been successfully scheduled with our team. We are here to provide you with expert guidance and support through the bail process.
                        </p>

                        <!-- Appointment Details Box -->
                        <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f3f4f6; border-radius: 8px; margin: 20px 0;">
                          <tr>
                            <td style="padding: 20px;">
                              <h2 style="color: #1f2937; font-size: 18px; margin: 0 0 15px 0; font-weight: bold;">Appointment Details</h2>
                              
                              <table width="100%" cellpadding="5" cellspacing="0">
                                <tr>
                                  <td style="color: #6b7280; font-size: 14px; padding: 5px 0;">
                                    <strong style="color: #374151;">Confirmation Number:</strong>
                                  </td>
                                  <td style="color: #1f2937; font-size: 14px; font-weight: bold; padding: 5px 0;">
                                    ${confirmationNumber}
                                  </td>
                                </tr>
                                <tr>
                                  <td style="color: #6b7280; font-size: 14px; padding: 5px 0;">
                                    <strong style="color: #374151;">Team Member:</strong>
                                  </td>
                                  <td style="color: #1f2937; font-size: 14px; padding: 5px 0;">
                                    ${agentName}
                                  </td>
                                </tr>
                                <tr>
                                  <td style="color: #6b7280; font-size: 14px; padding: 5px 0;">
                                    <strong style="color: #374151;">Date:</strong>
                                  </td>
                                  <td style="color: #1f2937; font-size: 14px; padding: 5px 0;">
                                    ${formattedDate}
                                  </td>
                                </tr>
                                <tr>
                                  <td style="color: #6b7280; font-size: 14px; padding: 5px 0;">
                                    <strong style="color: #374151;">Time:</strong>
                                  </td>
                                  <td style="color: #1f2937; font-size: 14px; padding: 5px 0;">
                                    ${appointmentTime}
                                  </td>
                                </tr>
                                <tr>
                                  <td style="color: #6b7280; font-size: 14px; padding: 5px 0;">
                                    <strong style="color: #374151;">Meeting Purpose:</strong>
                                  </td>
                                  <td style="color: #1f2937; font-size: 14px; padding: 5px 0;">
                                    ${purpose?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                                  </td>
                                </tr>
                                ${purposeDetails ? `
                                <tr>
                                  <td colspan="2" style="color: #6b7280; font-size: 14px; padding: 10px 0 5px 0;">
                                    <strong style="color: #374151;">Additional Details:</strong>
                                  </td>
                                </tr>
                                <tr>
                                  <td colspan="2" style="color: #1f2937; font-size: 14px; padding: 0 0 5px 0;">
                                    ${purposeDetails}
                                  </td>
                                </tr>
                                ` : ''}
                              </table>
                            </td>
                          </tr>
                        </table>

                        <!-- Important Note -->
                        <div style="background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 20px 0; border-radius: 4px;">
                          <p style="color: #92400e; font-size: 14px; margin: 0; line-height: 1.5;">
                            <strong>Important Note:</strong> If the team member you selected is unavailable or if another team member is better suited to assist you, we may suggest a different team member. You will be notified of any changes via email and will have the option to approve, decline, or reschedule your appointment.
                          </p>
                        </div>

                        <!-- Action Buttons -->
                        <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                          <tr>
                            <td align="center">
                              <a href="https://sailorsbail.com/appointment-scheduling-system" 
                                 style="display: inline-block; background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 8px; font-weight: bold; font-size: 16px; margin: 5px;">
                                Manage Appointment
                              </a>
                              <a href="tel:+19162563468" 
                                 style="display: inline-block; background-color: #dc2626; color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 8px; font-weight: bold; font-size: 16px; margin: 5px;">
                                Emergency Contact
                              </a>
                            </td>
                          </tr>
                        </table>

                        <p style="color: #6b7280; font-size: 14px; line-height: 1.6; margin: 20px 0 0 0;">
                          If you need to reschedule or have any questions, please call us at <strong style="color: #1f2937;">(916) 256-3468</strong>. We are available 24/7 for emergency assistance.
                        </p>
                      </td>
                    </tr>

                    <!-- Footer -->
                    <tr>
                      <td style="background-color: #f9fafb; padding: 20px 30px; text-align: center; border-top: 1px solid #e5e7eb;">
                        <p style="color: #6b7280; font-size: 12px; margin: 0 0 5px 0;">
                          Sailors Bail Bonds<br>
                          Professional Bail Services Since 1989
                        </p>
                        <p style="color: #9ca3af; font-size: 11px; margin: 5px 0 0 0;">
                          24/7 Emergency Bail Assistance: (916) 256-3468
                        </p>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </body>
          </html>
        `
      };

      // Call Supabase Edge Function to send email via Resend
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: emailContent
      });

      if (error) {
        throw new Error(`Failed to send confirmation email: ${error.message}`);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Email service error (confirmation):', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Send appointment reassignment notification to client
   * @param {Object} reassignmentData - Reassignment details
   * @returns {Promise<Object>} - Email send result
   */
  async sendAppointmentReassignment(reassignmentData) {
    try {
      const {
        clientName,
        clientEmail,
        originalAgentName,
        newAgentName,
        appointmentDate,
        appointmentTime,
        purpose,
        reassignmentReason,
        approvalToken,
        appointmentId
      } = reassignmentData;

      // Generate unique response links
      const baseUrl = window.location?.origin;
      const approveUrl = `${baseUrl}/appointment-response?token=${approvalToken}&action=approve`;
      const denyUrl = `${baseUrl}/appointment-response?token=${approvalToken}&action=deny`;
      const rescheduleUrl = `${baseUrl}/appointment-scheduling-system?reschedule=${appointmentId}`;
      const reassignUrl = `${baseUrl}/appointment-response?token=${approvalToken}&action=reassign`;


      const formattedDate = new Date(appointmentDate)?.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      const emailContent = {
        to: clientEmail,
        subject: `Appointment Reassignment - Action Required`,
        html: `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Appointment Reassignment</title>
          </head>
          <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
            <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px;">
              <tr>
                <td align="center">
                  <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                    <!-- Header -->
                    <tr>
                      <td style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); padding: 40px 30px; text-align: center;">
                        <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: bold;">Appointment Reassignment</h1>
                        <p style="color: #fef3c7; margin: 10px 0 0 0; font-size: 16px;">Action Required</p>
                      </td>
                    </tr>

                    <!-- Message -->
                    <tr>
                      <td style="padding: 30px;">
                        <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                          Hi ${clientName},
                        </p>
                        <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                          Your appointment has been reassigned to a different team member who is better suited to assist you with your specific needs.
                        </p>

                        <!-- Reassignment Details -->
                        <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #fef3c7; border-radius: 8px; margin: 20px 0;">
                          <tr>
                            <td style="padding: 20px;">
                              <h2 style="color: #92400e; font-size: 18px; margin: 0 0 15px 0; font-weight: bold;">Reassignment Details</h2>
                              
                              <table width="100%" cellpadding="5" cellspacing="0">
                                <tr>
                                  <td style="color: #78350f; font-size: 14px; padding: 5px 0;">
                                    <strong>Original Team Member:</strong>
                                  </td>
                                  <td style="color: #92400e; font-size: 14px; padding: 5px 0;">
                                    ${originalAgentName}
                                  </td>
                                </tr>
                                <tr>
                                  <td style="color: #78350f; font-size: 14px; padding: 5px 0;">
                                    <strong>New Team Member:</strong>
                                  </td>
                                  <td style="color: #92400e; font-size: 14px; font-weight: bold; padding: 5px 0;">
                                    ${newAgentName}
                                  </td>
                                </tr>
                                <tr>
                                  <td style="color: #78350f; font-size: 14px; padding: 5px 0;">
                                    <strong>Date:</strong>
                                  </td>
                                  <td style="color: #92400e; font-size: 14px; padding: 5px 0;">
                                    ${formattedDate}
                                  </td>
                                </tr>
                                <tr>
                                  <td style="color: #78350f; font-size: 14px; padding: 5px 0;">
                                    <strong>Time:</strong>
                                  </td>
                                  <td style="color: #92400e; font-size: 14px; padding: 5px 0;">
                                    ${appointmentTime}
                                  </td>
                                </tr>
                                <tr>
                                  <td style="color: #78350f; font-size: 14px; padding: 5px 0;">
                                    <strong>Purpose:</strong>
                                  </td>
                                  <td style="color: #92400e; font-size: 14px; padding: 5px 0;">
                                    ${purpose?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                                  </td>
                                </tr>
                                ${reassignmentReason ? `
                                <tr>
                                  <td colspan="2" style="color: #78350f; font-size: 14px; padding: 10px 0 5px 0;">
                                    <strong>Reason for Reassignment:</strong>
                                  </td>
                                </tr>
                                <tr>
                                  <td colspan="2" style="color: #92400e; font-size: 14px; padding: 0 0 5px 0;">
                                    ${reassignmentReason}
                                  </td>
                                </tr>
                                ` : ''}
                              </table>
                            </td>
                          </tr>
                        </table>

                        <!-- Action Required -->
                        <div style="background-color: #fee2e2; border-left: 4px solid #dc2626; padding: 15px; margin: 20px 0; border-radius: 4px;">
                          <p style="color: #991b1b; font-size: 14px; margin: 0; line-height: 1.5;">
                            <strong>Action Required:</strong> Please choose one of the options below to confirm your preferences for this appointment.
                          </p>
                        </div>

                        <!-- Action Buttons -->
                        <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                          <tr>
                            <td align="center">
                              <table cellpadding="0" cellspacing="0">
                                <tr>
                                  <td style="padding: 5px;">
                                    <a href="${approveUrl}" 
                                       style="display: inline-block; background-color: #16a34a; color: #ffffff; text-decoration: none; padding: 14px 28px; border-radius: 8px; font-weight: bold; font-size: 16px;">
                                      ✓ Approve New Team Member
                                    </a>
                                  </td>
                                </tr>
                                <tr>
                                  <td style="padding: 5px;">
                                    <a href="${denyUrl}" 
                                       style="display: inline-block; background-color: #dc2626; color: #ffffff; text-decoration: none; padding: 14px 28px; border-radius: 8px; font-weight: bold; font-size: 16px;">
                                      ✗ Deny and Cancel
                                    </a>
                                  </td>
                                </tr>
                                <tr>
                                  <td style="padding: 5px;">
                                    <a href="${rescheduleUrl}" 
                                       style="display: inline-block; background-color: #2563eb; color: #ffffff; text-decoration: none; padding: 14px 28px; border-radius: 8px; font-weight: bold; font-size: 16px;">
                                      📅 Reschedule Appointment
                                    </a>
                                  </td>
                                </tr>
                              </table>
                            </td>
                          	</tr>
                       	 	 <tr>
  			<td style="padding: 5px;">
    				    <a href="${reassignUrl}"
       				       style="display: inline-block; background-color: #2563eb; color: #ffffff; text-decoration: none; padding: 14px 28px; border-radius: 8px; font-weight: bold; font-size: 16px;">
      			🔄 Reassign to Another Team Member
    				    </a>
  			    </td>
				</tr>

			 </table>

                        <p style="color: #6b7280; font-size: 14px; line-height: 1.6; margin: 20px 0 0 0;">
                          If you have any questions or need immediate assistance, please call us at <strong style="color: #1f2937;">(916) 256-3468</strong>. We are available 24/7.
                        </p>
                      </td>
                    </tr>

                    <!-- Footer -->
                    <tr>
                      <td style="background-color: #f9fafb; padding: 20px 30px; text-align: center; border-top: 1px solid #e5e7eb;">
                        <p style="color: #6b7280; font-size: 12px; margin: 0 0 5px 0;">
                          Sailors Bail Bonds<br>
                          Professional Bail Services Since 1989
                        </p>
                        <p style="color: #9ca3af; font-size: 11px; margin: 5px 0 0 0;">
                          24/7 Emergency Bail Assistance: (916) 256-3468
                        </p>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </body>
          </html>
        `
      };

      // Call Supabase Edge Function
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: emailContent
      });

      if (error) {
        throw new Error(`Failed to send reassignment email: ${error.message}`);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Email service error (reassignment):', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Send appointment cancellation notification to client
   * @param {Object} cancellationData - Cancellation details
   * @returns {Promise<Object>} - Email send result
   */
  async sendAppointmentCancellation(cancellationData) {
    try {
      const {
        clientName,
        clientEmail,
        agentName,
        appointmentDate,
        appointmentTime,
        purpose,
        cancellationReason
      } = cancellationData;

      const formattedDate = new Date(appointmentDate)?.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      const emailContent = {
        to: clientEmail,
        subject: `Appointment Cancellation Notice`,
        html: `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Appointment Cancelled</title>
          </head>
          <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
            <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px;">
              <tr>
                <td align="center">
                  <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                    <!-- Header -->
                    <tr>
                      <td style="background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); padding: 40px 30px; text-align: center;">
                        <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: bold;">Appointment Cancelled</h1>
                        <p style="color: #fecaca; margin: 10px 0 0 0; font-size: 16px;">Sailors Bail Bonds</p>
                      </td>
                    </tr>

                    <!-- Message -->
                    <tr>
                      <td style="padding: 30px;">
                        <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                          Hi ${clientName},
                        </p>
                        <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                          We are writing to confirm that your appointment has been cancelled as requested.
                        </p>

                        <!-- Cancelled Appointment Details -->
                        <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #fee2e2; border-radius: 8px; margin: 20px 0;">
                          <tr>
                            <td style="padding: 20px;">
                              <h2 style="color: #991b1b; font-size: 18px; margin: 0 0 15px 0; font-weight: bold;">Cancelled Appointment Details</h2>
                              
                              <table width="100%" cellpadding="5" cellspacing="0">
                                <tr>
                                  <td style="color: #7f1d1d; font-size: 14px; padding: 5px 0;">
                                    <strong>Team Member:</strong>
                                  </td>
                                  <td style="color: #991b1b; font-size: 14px; padding: 5px 0;">
                                    ${agentName}
                                  </td>
                                </tr>
                                <tr>
                                  <td style="color: #7f1d1d; font-size: 14px; padding: 5px 0;">
                                    <strong>Date:</strong>
                                  </td>
                                  <td style="color: #991b1b; font-size: 14px; padding: 5px 0;">
                                    ${formattedDate}
                                  </td>
                                </tr>
                                <tr>
                                  <td style="color: #7f1d1d; font-size: 14px; padding: 5px 0;">
                                    <strong>Time:</strong>
                                  </td>
                                  <td style="color: #991b1b; font-size: 14px; padding: 5px 0;">
                                    ${appointmentTime}
                                  </td>
                                </tr>
                                <tr>
                                  <td style="color: #7f1d1d; font-size: 14px; padding: 5px 0;">
                                    <strong>Purpose:</strong>
                                  </td>
                                  <td style="color: #991b1b; font-size: 14px; padding: 5px 0;">
                                    ${purpose?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                                  </td>
                                </tr>
                                ${cancellationReason ? `
                                <tr>
                                  <td colspan="2" style="color: #7f1d1d; font-size: 14px; padding: 10px 0 5px 0;">
                                    <strong>Cancellation Reason:</strong>
                                  </td>
                                </tr>
                                <tr>
                                  <td colspan="2" style="color: #991b1b; font-size: 14px; padding: 0 0 5px 0;">
                                    ${cancellationReason}
                                  </td>
                                </tr>
                                ` : ''}
                              </table>
                            </td>
                          </tr>
                        </table>

                        <!-- Rebooking Information -->
                        <div style="background-color: #dbeafe; border-left: 4px solid #2563eb; padding: 15px; margin: 20px 0; border-radius: 4px;">
                          <p style="color: #1e40af; font-size: 14px; margin: 0; line-height: 1.5;">
                            <strong>Need to Rebook?</strong> We understand that circumstances change. If you would like to schedule a new appointment, simply click the button below or call us directly.
                          </p>
                        </div>

                        <!-- Action Buttons -->
                        <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                          <tr>
                            <td align="center">
                              <a href="https://sailorsbail.com/appointment-scheduling-system" 
                                 style="display: inline-block; background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 8px; font-weight: bold; font-size: 16px; margin: 5px;">
                                Schedule New Appointment
                              </a>
                              <a href="tel:+19162563468" 
                                 style="display: inline-block; background-color: #dc2626; color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 8px; font-weight: bold; font-size: 16px; margin: 5px;">
                                Call Us: (916) 256-3468
                              </a>
                            </td>
                          </tr>
                        </table>

                        <p style="color: #6b7280; font-size: 14px; line-height: 1.6; margin: 20px 0 0 0;">
                          If you have any questions or need immediate assistance, we are available 24/7 at <strong style="color: #1f2937;">(916) 256-3468</strong>. Thank you for considering Sailors Bail Bonds.
                        </p>
                      </td>
                    </tr>

                    <!-- Footer -->
                    <tr>
                      <td style="background-color: #f9fafb; padding: 20px 30px; text-align: center; border-top: 1px solid #e5e7eb;">
                        <p style="color: #6b7280; font-size: 12px; margin: 0 0 5px 0;">
                          Sailors Bail Bonds<br>
                          Professional Bail Services Since 1989
                        </p>
                        <p style="color: #9ca3af; font-size: 11px; margin: 5px 0 0 0;">
                          24/7 Emergency Bail Assistance: (916) 256-3468
                        </p>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </body>
          </html>
        `
      };

      // Call Supabase Edge Function
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: emailContent
      });

      if (error) {
        throw new Error(`Failed to send cancellation email: ${error.message}`);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Email service error (cancellation):', error);
      return { success: false, error: error?.message };
    }
  }
};
